import { useState } from "react";

import { downloadExportedFile } from "./export.service";
import type { ExportedFile, ExportFormat, FileExportOptions } from "./export.types";

export interface UseFileExportResult {
  isOpen: boolean;
  isExporting: boolean;
  open: () => void;
  close: () => void;
  run: (format: ExportFormat, options?: FileExportOptions) => Promise<void>;
}

export interface UseFileExportOptions {
  onSuccess: (fileName: string, format: ExportFormat) => void;
  onEmpty: () => void;
  onError: (error: unknown, format: ExportFormat) => void;
  /**
   * Solicita ao serviço o arquivo da visualização atual (filtros, colunas
   * visíveis e ordenação), com o conjunto completo do resultado (RN003).
   */
  loadFile: (format: ExportFormat, options?: FileExportOptions) => Promise<ExportedFile>;
  /** Nome base do arquivo quando o serviço não informa um. */
  fileName: string;
  /** A visualização atual não tem registros: nada é solicitado ao serviço. */
  isEmpty?: () => boolean;
}

/**
 * Exportar Visualização com o arquivo gerado no servidor (US 1442): a tela
 * informa os critérios em vigor e o front apenas realiza o download.
 */
export function useFileExport({
  loadFile,
  fileName,
  isEmpty,
  onSuccess,
  onEmpty,
  onError,
}: UseFileExportOptions): UseFileExportResult {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  function open() {
    setIsOpen(true);
  }

  function close() {
    if (isExporting) return;

    setIsOpen(false);
  }

  /** Nunca rejeita: a falha mantém o modal aberto para nova tentativa. */
  async function run(format: ExportFormat, options?: FileExportOptions) {
    if (isEmpty?.()) {
      setIsOpen(false);
      onEmpty();
      return;
    }

    setIsExporting(true);

    try {
      const file = await loadFile(format, options);

      onSuccess(downloadExportedFile(file, fileName, format), format);
      setIsOpen(false);
    } catch (error) {
      onError(error, format);
    } finally {
      setIsExporting(false);
    }
  }

  return { isOpen, isExporting, open, close, run };
}
